import { getIntegrationsPage } from "queries/Integrations";
import { GetStaticPropsContext } from "next";
import Container from "components/core/Container";
import TextBlock, { ITextBlock } from "blocks/text/TextBlock";
import Heading from "components/elements/Heading";
import Paragraph from "components/elements/Paragraph";
import Button from "components/elements/Button";
import CtaBlock from "blocks/cta/CtaBlock";
import React, { useMemo, useRef, useState } from "react";
import useAutoClose from "components/hooks/useAutoClose";
import IntegrationCard, { IIntegrationsCard } from "components/integrations/IntegrationCard";
import SortByButton from "components/integrations/SortbyButton";
import IntegrationsCategories from "components/integrations/IntegrationCategories";
import cn from "classnames";
import { localIcon } from "components/elements/Icon";
import { withAppLayout } from "components/core/withAppLayout";
import useTrans from "components/hooks/useTrans";
import { createSubscription } from "components/hooks/usePreview";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../../next-i18next.config";
import { useSpring, animated } from 'react-spring';
import Link from "components/base/Link";

export async function getStaticProps(context: GetStaticPropsContext) {
    const query = {
        query: getIntegrationsPage(context.locale),
        preview: context.preview,
        withAppData: true,
        locale: context.locale
    };

    const data = await createSubscription(context, query);

    return {
        props: {
            data: data,
            seo: data.initialData.integrationPage.seo,
            appData: {
                ...data.initialData.header,
                ...data.initialData.footer,
            },
            ...(await serverSideTranslations(context.locale, ['common'], nextI18NextConfig)),
        }
    };
}

export type IIntegrationCategory = {
    category: string
};

function Index(props: any) {
    const page = props.integrationPage;
    const cta = props.integrationPage.cta[0];

    const { t } = useTrans();
    const rootEl = useRef();

    const [dropdownActive, setDropdownActive] = useState(false);
    const [categoryActive, setCategoryActive] = useState('');
    const [sortByActive, setSortByActive] = useState('');
    const [searchValue, setSearchValue] = useState('');

    const categories = page.categories.map((c: IIntegrationCategory) => c.category);
    const imageAppear = useSpring( { from: { opacity: .5, marginTop: "10rem" }, to: { opacity: 1, marginTop: "0rem" }, delay: 50 });


    const cardAlignmentCn = cn(
        'sm:box sm:col-start-4 sm:col-end-13 xl:col-start-4 xl:col-end-13'
    );

    const integrationsAlignmentCn = cn(
        'flex flex-col sm:box sm:grid sm:grid-cols-12 sm:auto-rows-auto'
    );

    const cardGridCn = cn(
        'sm:grid sm:grid-cols-3 sm:mb-8 gap-8'
    );

    const sortBy = (integrationsToSort: IIntegrationsCard[]) => {
        switch (sortByActive) {
            case t('price'):
                return (
                    integrationsToSort.sort((a: IIntegrationsCard, b: IIntegrationsCard) => b.price[0].price - a.price[0].price)
                );
            case t('ratings'):
                return (
                    integrationsToSort.sort((a: any, b: any) => b.rating - a.rating)
                );
            case t('popular'):
                return (
                    integrationsToSort
                );
            default:
                return (
                    integrationsToSort
                );
        }
    };

    const filteredIntegrations = useMemo(() => {
        let integrations: IIntegrationsCard[] = props.allIntegrationsCards;

        if (searchValue) {
            integrations = integrations.filter((c) => c.name.toLowerCase().match((searchValue?.toLowerCase())));
        }

        if (categoryActive) {
            integrations = integrations.filter((c) => {
                return c.category.find(category => category.category == categoryActive);
            });
        }

        if (sortByActive) {
            integrations = sortBy(integrations);
        }

        return integrations;
    }, [searchValue, sortByActive, categoryActive, dropdownActive]);

    useAutoClose(
        rootEl,
        () => {
            setDropdownActive(false);
        },
        dropdownActive
    );

    return (
        <div className={'border-b border-gray pt-10'}>
            <div>
                {page.textBlock.map((textBlock: ITextBlock) => {
                    return (
                        <div key={textBlock.id}>
                            <TextBlock key={textBlock.id} {...textBlock} className={'sm:w-3/5'}/>
                        </div>
                    );
                })}
                <Container>
                    <input
                        className={'w-full p-3 border border-purple-tint rounded-lg sm:w-1/2 lg:w-2/5'}
                        placeholder={t('searchBarPlaceholder')}
                        onChange={(event) => setSearchValue(event.target.value)}
                    />
                </Container>
            </div>

            <Container className={'lg:pt-10'}>
                <div className={integrationsAlignmentCn}>
                    <IntegrationsCategories
                        categories={categories}
                        activeCategory={categoryActive}
                        setActiveCategory={setCategoryActive}
                    />

                    <div className={cardAlignmentCn}>
                        <div className={cardGridCn}>
                            {filteredIntegrations.map((card: IIntegrationsCard) => {
                                return (
                                    <animated.div key={card.id} style={imageAppear}>
                                        <IntegrationCard card={card} />
                                    </animated.div>
                                );
                            })}
                        </div>
                    </div>

                    <div
                        className="hidden invisible sm:visible sm:grid box col-start-4 col-end-12 xl:col-start-4 xl:col-end-10 mb-4 mt-8"
                    >
                        <div className={'flex flex-col mb-3'}>
                            <Heading renderAs={'h3'} className={'mb-2'} fontWeight={'medium'}>
                                {t('cantFindWhatYouNeed')}
                            </Heading>
                            <Paragraph className={'mb-2'} fontWeight={'light'}>
                                {t('buildYourOwnApps')}
                            </Paragraph>
                            <div className={'flex flex-row mr-16'}>
                                <Link to={'https://docs.piggy.eu/'}>
                                    <Button
                                        id={"integrations__explore-the-api"}
                                        variant={'link'}
                                        size={'medium'}
                                        color={'purple'}
                                        className={'mr-8'}
                                        icon={localIcon("chevron-right")}
                                    >
                                        {t('exploreTheApi')}
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </Container>

            <div className={'invisible hidden sm:visible sm:grid pt-8'}>
                {cta && (
                    <section>
                        <CtaBlock {...cta} />
                    </section>
                )}
            </div>
        </div>
    );
}

export default withAppLayout(Index);